import React, { useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Modal from '@components/shared/Modal';
import TextLinkButton from '@components/shared/TextLinkButton';
import useDesktop from '@components/shared/useDesktop';
import Video from '@components/VideoModule';
import { VIDEO_PLAY_BUTTON_2 } from '@constants';
import { PageSlice, SliceTimelineDetails, SliceTimelineItem } from '@contracts';
import handleTextReplacement from '@utils/handleTextReplacement';
import useGsapTimeline from '@utils/useGsapTimeline';
import Colors from '../Colors';
import LazyLoadVideo from '../LazyLoadVideo';
import LinkButton from '../LinkButton';
import { TEXT_STYLES, TextVariant } from './Typography';

interface TimelineProps {
  slice: PageSlice;
}

const Timeline: React.FC<TimelineProps> = ({ slice }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalData, setModalData] = useState({ open: false, videoPreviewUrl: '', videoUrl: '' });
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SliceTimelineDetails;
  const sliceItems = slice.items as SliceTimelineItem[];
  const DESKTOP = 1200;
  const isDesktop = useDesktop(DESKTOP);

  useGsapTimeline({ isDesktop, setActiveIndex, containerRef });

  return (
    <TimelineContainer ref={containerRef}>
      <Wrapper>
        {sliceDetails?.overline && <Overline>{sliceDetails.overline}</Overline>}
        {sliceDetails?.heading?.text && <Heading>{sliceDetails.heading.text}</Heading>}
        {sliceDetails?.subheading?.text && <SubHeading>{sliceDetails.subheading.text}</SubHeading>}

        <TimelineA>
          {sliceItems?.map((item, index) => (
            <TimelineItem className="timeline-item" key={index}>
              {index !== sliceItems.length - 1 && <Line></Line>}
              {index !== sliceItems.length - 1 && <ProgressLine className="progressline"></ProgressLine>}
              <TimelineItemTextWrap>
                <div>
                  <TimelinePointMobile>{index + 1}</TimelinePointMobile>
                  <h4>{handleTextReplacement(item.heading.text)}</h4>
                </div>
                <p>{handleTextReplacement(item.bodycopy?.text)}</p>

                {item.video_link_text_key && item.video_url?.url ? (
                  <VideoButtonWrapper
                    onClick={() =>
                      setModalData({
                        open: true,
                        videoPreviewUrl: item.video_preview_image?.url || '',
                        videoUrl: item.video_url?.url || ''
                      })
                    }
                  >
                    <TextLinkButton $color={Colors.lilac}>{t(item.video_link_text_key)}</TextLinkButton>
                    {item.link_icon && <LazyLoadImage src={VIDEO_PLAY_BUTTON_2} alt={'video play'} />}
                  </VideoButtonWrapper>
                ) : null}
              </TimelineItemTextWrap>
              <TimelinePoint $active={index === activeIndex}>{index + 1}</TimelinePoint>
              <img src={item.image.url} alt={item.image.alt} />
            </TimelineItem>
          ))}
          {sliceDetails.button_url && sliceDetails.button_text_key && (
            <ButtonWrapper>
              <LinkButton color={Colors.lilac} to={sliceDetails.button_url} isInline>
                {sliceDetails.button_text_key}
              </LinkButton>
            </ButtonWrapper>
          )}
        </TimelineA>
        <Modal
          title={''}
          modalOpen={modalData.open}
          paddingDesktop="0"
          paddingMobile="0"
          onClose={() =>
            setModalData({
              open: false,
              videoPreviewUrl: '',
              videoUrl: ''
            })
          }
          width="580px"
        >
          <VideoWrapper $backgroundImage={modalData.videoPreviewUrl}>
            <LazyLoadVideo>
              <Video mp4={modalData.videoUrl} autoplay={false} controls={true} muted={false} loop={false} />
            </LazyLoadVideo>
          </VideoWrapper>
        </Modal>
      </Wrapper>
    </TimelineContainer>
  );
};

export default Timeline;

const TimelineContainer = styled.div``;

const Wrapper = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  color: ${Colors.navyBlue};
`;

const Overline = styled.div`
  ${TEXT_STYLES[TextVariant.UpperText4]};
  text-align: center;
  margin: 0 auto 16px;
`;

const Heading = styled.h2`
  ${TEXT_STYLES[TextVariant.Heading4]};
  text-align: center;
  margin: 0 auto 16px;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const SubHeading = styled.h2`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: center;
  max-width: 640px;
  margin: 0 auto;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;

const TimelineA = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 100px;
  margin-top: 48px;

  @media (max-width: 1200px) {
    row-gap: 24px;
  }

  > div:nth-of-type(2n) {
    flex-direction: row-reverse;

    @media (max-width: 1200px) {
      flex-direction: column-reverse;
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 300px;
  text-align: left;
  position: relative;

  @media (max-width: 1200px) {
    flex-direction: column-reverse;
    height: auto;
  }

  > img {
    width: 500px;
    height: 300px;
    border-radius: 12px;
    object-fit: cover;

    @media (max-width: 1200px) {
      border-radius: 8px 8px 0 0;
    }

    @media (max-width: 600px) {
      height: 200px;
    }
  }

  h4 {
    ${TEXT_STYLES[TextVariant.Heading5]};
    margin: 0;

    @media (max-width: 1200px) {
      ${TEXT_STYLES[TextVariant.BodyBoldLG]};
    }
  }

  p {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;
const TimelineItemTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 16px;
  max-width: 500px;
  width: 100%;
  color: ${Colors.navyBlue};

  > div:nth-of-type(1) {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 16px;
  }

  > p {
    color: ${Colors.navyBlue};
    /* TODO: Discuss with Val if this is needed, 
    it's introducing complexity and doesn't look much better
    
    @media (max-width: 600px) {
      display: none;
      opacity: 0;
      height: 0;
    } */
  }

  @media (max-width: 1200px) {
    background-color: ${Colors.paleGold25};
    border-radius: 0 0 8px 8px;
    padding: 24px 16px;
    width: 100%;
  }
`;

const TimelinePoint = styled.div<{ $active?: boolean }>`
  ${TEXT_STYLES[TextVariant.UpperText3]};
  width: ${(props) => (!props.$active ? '32px' : '40px')};
  height: ${(props) => (!props.$active ? '32px' : '40px')};
  border-radius: 50%;
  background-color: ${(props) => (props.$active ? Colors.paleGold : Colors.paleGold50)};
  line-height: ${(props) => (!props.$active ? '32px' : '40px')};
  text-align: center;
  padding: 1px 0 0 2px;
  transition: width 0.3s ease, height 0.3s ease, background-color 0.3s ease, line-height 0.3s ease;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const TimelinePointMobile = styled(TimelinePoint)`
  display: none;
  background-color: ${Colors.paleGold};
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    display: block;
  }
`;

const Line = styled.div`
  width: 3px;
  height: 135%;
  background-color: ${Colors.paleGold25};
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: -1;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const ProgressLine = styled(Line)`
  background-color: ${Colors.paleGold};
  height: 4%;
`;

const VideoButtonWrapper = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  cursor: pointer;
`;

const VideoWrapper = styled.div<{ $backgroundImage: string }>`
  border-radius: 2px;
  background: ${Colors.sky};

  & .video-container {
    height: 100%;
    position: relative;
  }

  & .video-wrapper {
    padding-bottom: 68%;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
